import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoNotFoundException } from "@/shared/http";

import { MeteringReport } from "..";

interface UseMeteringOptions {
  maxCount?: number;
}

export function useMetering(options?: Partial<UseMeteringOptions>) {
  const { currentOrganization } = useAuth();
  const { meteringService } = useDomainServices();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reports, setReports] = useState<
    MeteringReport | MeteringReport[] | null
  >(null);

  const getMetering = useCallback(
    async (signal: AbortSignal) => {
      try {
        if (!currentOrganization || !currentOrganization.org_id) {
          throw new Error("Organization not set");
        }

        const searchParams = new URLSearchParams();

        if (options?.maxCount) {
          searchParams.append("max_count", options.maxCount.toString());
        }

        const reports = await meteringService.getMeteringReportForOrg(
          currentOrganization.org_id,
          { abortSignal: signal, searchParams: searchParams },
        );

        setReports(reports);
        setError(null);
      } catch (error) {
        if (signal.aborted) {
          return; // Prevent state update if aborted
        }

        if (error instanceof RobotoNotFoundException) {
          // The backend 404s when no MTD report has been generated for the
          // org yet (e.g. a stack where metering was just enabled). That's
          // "no data yet", not an error worth breaking the usage page over.
          setReports(null);
          setError(null);
          return;
        }

        if (error instanceof Error) {
          if (error.name === "AbortError") {
            return;
          }

          setError(error);
        } else {
          setError(new Error(String(error), { cause: error }));
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    },
    [currentOrganization, meteringService, options?.maxCount],
  );

  useEffect(() => {
    const abortController = new AbortController();
    void getMetering(abortController.signal);
    return () => abortController.abort();
  }, [getMetering]);

  return {
    reports,
    loading,
    error,
  };
}
